import type { Permission } from '@vmc/shared';
import type { ComponentType } from 'react';
import { createBrowserRouter, type LazyRouteFunction, type RouteObject } from 'react-router';
import { AppShell } from './layout/app-shell';
import { RouteError } from './route-error';
import { RequireAuth, RequirePermission } from './route-guards';

/** Lazy route whose module default-exports the page; the permission check wraps it. */
function page(
  load: () => Promise<{ default: ComponentType }>,
  permission?: Permission,
): LazyRouteFunction<RouteObject> {
  return async () => {
    const { default: Page } = await load();
    return {
      Component: () => (
        <RequirePermission permission={permission}>
          <Page />
        </RequirePermission>
      ),
    };
  };
}

export const router = createBrowserRouter([
  {
    ErrorBoundary: RouteError,
    children: [
      { path: '/login', lazy: page(() => import('@/features/auth/login-page')) },
      {
        path: '/',
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          {
            ErrorBoundary: RouteError,
            children: [
              {
                index: true,
                lazy: page(() => import('@/features/dashboard/dashboard-page'), 'dashboard:view'),
              },
              {
                path: 'chats/:conversationId?',
                lazy: page(() => import('@/features/chats/chats-page'), 'chats:handle'),
              },
              {
                path: 'citizens',
                lazy: page(() => import('@/features/citizens/citizens-page'), 'citizens:read'),
              },
              {
                path: 'divisions',
                lazy: page(() => import('@/features/divisions/divisions-page'), 'divisions:write'),
              },
              {
                path: 'kb',
                lazy: page(() => import('@/features/kb/pages/kb-layout'), 'kb:read'),
                children: [
                  { index: true, lazy: page(() => import('@/features/kb/pages/kb-entries-page')) },
                  {
                    path: 'unanswered',
                    lazy: page(() => import('@/features/kb/pages/kb-unanswered-page')),
                  },
                  { path: 'test', lazy: page(() => import('@/features/kb/pages/kb-test-page')) },
                  {
                    path: 'scrape',
                    lazy: page(() => import('@/features/kb/pages/kb-scrape-page')),
                  },
                ],
              },
              {
                path: 'kb/new',
                lazy: page(() => import('@/features/kb/pages/kb-entry-page'), 'kb:write'),
              },
              {
                path: 'kb/:entryId',
                lazy: page(() => import('@/features/kb/pages/kb-entry-page'), 'kb:read'),
              },
              {
                path: 'templates',
                lazy: page(
                  () => import('@/features/templates/pages/templates-page'),
                  'templates:read',
                ),
              },
              {
                path: 'templates/new',
                lazy: page(
                  () => import('@/features/templates/pages/template-editor-page'),
                  'templates:write',
                ),
              },
              {
                path: 'templates/:templateId',
                lazy: page(
                  () => import('@/features/templates/pages/template-editor-page'),
                  'templates:read',
                ),
              },
              {
                path: 'broadcasts',
                lazy: page(
                  () => import('@/features/broadcasts/pages/broadcasts-page'),
                  'broadcasts:read',
                ),
              },
              {
                path: 'broadcasts/new',
                lazy: page(
                  () => import('@/features/broadcasts/pages/broadcast-new-page'),
                  'broadcasts:send',
                ),
              },
              {
                path: 'broadcasts/:broadcastId',
                lazy: page(
                  () => import('@/features/broadcasts/pages/broadcast-detail-page'),
                  'broadcasts:read',
                ),
              },
              {
                path: 'users',
                lazy: page(() => import('@/features/users/users-page'), 'users:manage'),
              },
              {
                path: 'settings',
                lazy: page(() => import('@/features/settings/settings-page'), 'settings:manage'),
              },
              {
                path: 'audit',
                lazy: page(() => import('@/features/audit/audit-page'), 'audit:read'),
              },
              { path: '*', lazy: page(() => import('./not-found')) },
            ],
          },
        ],
      },
    ],
  },
]);
